/**
 * Announcement illustrations: miniature product scenes composed from real UI
 * primitives, in the style of the marketing highlight mocks. One idea per
 * scene, tokens only, decoration for the copy (`aria-hidden` where the scene
 * owns its container). Modal heroes render the full dialog header (height,
 * background, bottom border); card art renders a small standalone scene.
 */

/** The Build palette: block chips with Schedule highlighted. */
function BlocksMiniCard() {
  return (
    <div className="w-[110px] rounded-lg border border-border bg-background p-2 shadow-sm">
      <p className="px-0.5 text-[8px] font-medium text-muted-foreground">Blocks</p>
      <div className="mt-1 grid grid-cols-2 gap-1">
        <span className="rounded-md border border-border py-1.5 text-center text-[8px] text-foreground">
          Heading
        </span>
        <span className="rounded-md border border-border py-1.5 text-center text-[8px] text-foreground">
          Image
        </span>
        <span className="rounded-md border border-border py-1.5 text-center text-[8px] text-foreground">
          Catalog
        </span>
        <span className="rounded-md border border-primary/50 bg-primary/10 py-1.5 text-center text-[8px] font-medium text-primary dark:border-brand-accent/50 dark:bg-brand-accent/10 dark:text-brand-accent">
          Schedule
        </span>
      </div>
    </div>
  );
}

/** The Performance funnel: viewed, clicked, checked out. */
function PerformanceMiniCard() {
  return (
    <div className="w-[124px] rounded-lg border border-border bg-background p-2.5 shadow-sm">
      <p className="text-[8px] font-medium uppercase tracking-wide text-muted-foreground">
        Performance
      </p>
      <div className="mt-1.5 space-y-1">
        <div className="flex items-center gap-1">
          <span className="h-2 w-2/5 rounded-sm bg-primary/70 dark:bg-brand-accent/70" />
          <span className="whitespace-nowrap text-[7px] text-muted-foreground">214 viewed</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="h-2 w-1/5 rounded-sm bg-primary/50 dark:bg-brand-accent/50" />
          <span className="whitespace-nowrap text-[7px] text-muted-foreground">63 clicked</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="h-2 w-[8%] rounded-sm bg-primary/30 dark:bg-brand-accent/30" />
          <span className="whitespace-nowrap text-[7px] text-muted-foreground">12 checked out</span>
        </div>
      </div>
      <p className="mt-1.5 text-[7px] text-green-600 dark:text-green-400">Checkout rate 5.6%</p>
    </div>
  );
}

/** A published intro-offer page: heading, catalog row, checkout CTA. */
function OfferPageMiniCard() {
  return (
    <div className="w-[180px] rounded-xl border border-border bg-background p-3 shadow-lg">
      <p className="text-[12px] font-semibold text-foreground">New member special</p>
      <div className="mt-2 flex items-center justify-between rounded-md border border-border px-2 py-1.5">
        <span className="text-[9px] font-medium text-foreground">2 weeks unlimited</span>
        <span className="text-[9px] text-muted-foreground">$49</span>
      </div>
      <div className="mt-1.5 rounded-md bg-primary py-1.5 text-center text-[9px] font-medium text-primary-foreground dark:bg-brand-accent dark:text-brand-accent-foreground">
        Claim offer
      </div>
    </div>
  );
}

/**
 * Modal hero for the Pages release: the offer page front and center, the
 * block palette and Performance funnel fanned behind it. The side cards
 * start tucked behind the offer page and fan out as the modal settles
 * (the `--tw-enter-*` variables are the animation's starting transform).
 */
export function IntroducingPagesHero() {
  return (
    <div
      aria-hidden="true"
      className="relative flex h-44 items-center justify-center overflow-hidden border-b border-border bg-muted/40"
    >
      <div className="absolute left-1/2 top-1/2 -translate-x-[158%] -translate-y-[55%] -rotate-6 animate-in fade-in-0 fill-mode-backwards delay-150 duration-300 ease-out motion-reduce:animate-none [--tw-enter-translate-x:-60%] [--tw-enter-translate-y:-55%]">
        <BlocksMiniCard />
      </div>
      <div className="absolute left-1/2 top-1/2 -translate-y-[45%] translate-x-[52%] rotate-6 animate-in fade-in-0 fill-mode-backwards delay-150 duration-300 ease-out motion-reduce:animate-none [--tw-enter-translate-x:-25%] [--tw-enter-translate-y:-45%]">
        <PerformanceMiniCard />
      </div>
      <div className="relative">
        <OfferPageMiniCard />
      </div>
    </div>
  );
}
